import type { AccordionFontSchemaType, AccordionSchemaType } from '@vedalib/editor/lib/brand'
import type {
  AccordionElementValue,
  EditorElement,
  RichTextPanelItem,
} from '@vedalib/editor/lib/elements'
import type { RichTextValue } from '@vedalib/rich-text'
import { textToRtValue } from '@vedalib/rich-text'
import type { Variants } from 'framer-motion'
import { motion } from 'framer-motion'
import * as R from 'ramda'
import { useCallback } from 'react'

import Node from 'components/Editor/cource/layout/Node'
import RichText from 'components/form/RichText'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import type { Block, IBlockMode } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import ContentStyled from './styled/ContentStyled'
import IconStyled from './styled/IconStyled'
import ItemStyled from './styled/ItemStyled'
import TitleStyled from './styled/TitleStyled'

const ANIMATION_VARIANTS: Variants = {
  open: {
    height: 'auto',
    opacity: 1,
    translateY: 0,
    position: 'relative',
    zIndex: 0,
    transition: { duration: 0.3 },
    willChange: 'auto',
  },
  closed: {
    height: 0,
    opacity: 0,
    translateY: -10,
    paddingTop: 0,
    paddingBottom: 0,
    position: 'relative',
    zIndex: 0,
    transition: { duration: 0.3, opacity: { duration: 0.5 } },
    willChange: 'auto',
  },
}

interface IItemProps {
  styles: ElementStyleCss<AccordionSchemaType>
  item: RichTextPanelItem
  index: number
  waiting?: boolean
  block?: Block | null
  isOpen?: boolean
  element: EditorElement<AccordionElementValue, AccordionSchemaType, AccordionFontSchemaType>
  font: ElementFontCss<AccordionFontSchemaType>
  mode: IBlockMode
  activeRt?: boolean
  onChange?: (value: AccordionElementValue) => void
  onLabelSelect?: (index: string) => void
  onChoose?: (value: string) => void
  onToggle?: (value: string) => void
}

const AccordionElementItem: React.FC<IItemProps> = (props) => {
  const { styles, item, index, waiting, block, isOpen, element, font, mode, activeRt } = props
  const { onChange, onChoose, onToggle, onLabelSelect } = props
  const { value, label } = item
  const { icon } = styles
  const showIcon = !((!isOpen && icon.backgroundImage) || (isOpen && icon.backgroundOpen))
  const isEditor = mode.previewMode === PreviewMode.editor
  const isFill = isEditor && mode.editorMode === AppMode.fill

  const handleLabelSelect = useCallback(
    () => onLabelSelect?.(index.toString()),
    [onLabelSelect, index],
  )

  const handleLabelChange = useCallback(
    (v: RichTextValue) => onChange?.(R.assocPath(['items', index, 'label'], v, element.value)),
    [onChange, element.value, index],
  )

  const handleToggleIcon = useCallback(
    () => isEditor && onToggle?.(value),
    [onToggle, isEditor, value],
  )

  const handleToggleTitle = useCallback(
    () => !isEditor && onToggle?.(value),
    [onToggle, isEditor, value],
  )

  const handleChoose = useCallback(() => onChoose?.(value), [onChoose, value])

  const contentId = `accordion-${element.id}-${index}-content`
  const titleId = `accordion-${element.id}-${index}-title`

  return (
    <ItemStyled $styles={styles} onClick={handleChoose}>
      <h2>
        <TitleStyled
          $isOpen={isOpen}
          $styles={styles}
          aria-controls={contentId}
          aria-expanded={!!isOpen}
          id={titleId}
          onClick={handleToggleTitle}
        >
          <IconStyled $isOpen={isOpen} $styles={styles} onClick={handleToggleIcon}>
            {showIcon && <Icon name='iconsForElementsDown' size={KitSize.S} aria-hidden />}
          </IconStyled>
          <span onSelect={handleLabelSelect}>
            <RichText
              active={!!activeRt}
              disabled={!isFill}
              isPreview={mode?.previewMode !== PreviewMode.editor}
              name={`accordion.${index}`}
              onChange={handleLabelChange}
              styles={font}
              value={label || textToRtValue(t('elements.accordion.form.component'))}
              waiting={waiting}
            />
          </span>
        </TitleStyled>
      </h2>

      <motion.div
        animate={isOpen ? 'open' : 'closed'}
        initial='closed'
        style={{ overflow: 'hidden' }}
        variants={ANIMATION_VARIANTS}
        {...(!isOpen && { inert: '' })}
      >
        <ContentStyled $styles={styles} aria-labelledby={titleId} id={contentId} role='region'>
          <Node block={block!} id={value} mode={mode} isItem />
        </ContentStyled>
      </motion.div>
    </ItemStyled>
  )
}

export default AccordionElementItem
